import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Divider from "@material-ui/core/Divider";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Alert from "@material-ui/lab/Alert";
import { Add, Delete, FileCopy, FolderOpen, Inbox } from "@material-ui/icons";
import Switch from "@material-ui/core/Switch";
import Auth from "../../middleware/Auth";
import API from "../../middleware/Api";
import { toggleSnackbar } from "../../redux/explorer";
import { formatLocalTime } from "../../utils/datetime";
import { useTranslation } from "react-i18next";
import PathSelector from "../FileManager/PathSelector";

const expiresOptions = [
    { label: "never", value: 0 },
    { label: "7days", value: 7 },
    { label: "30days", value: 30 },
    { label: "90days", value: 90 },
    { label: "365days", value: 365 },
];

const useStyles = makeStyles((theme) => ({
    layout: {
        width: "auto",
        marginTop: "50px",
        marginLeft: theme.spacing(3),
        marginRight: theme.spacing(3),
        [theme.breakpoints.up(1100 + theme.spacing(3) * 2)]: {
            width: 1100,
            marginLeft: "auto",
            marginRight: "auto",
        },
        marginBottom: "50px",
    },
    content: {
        marginTop: theme.spacing(4),
    },
    cardContent: {
        padding: theme.spacing(3),
    },
    infoRow: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(2),
        flexWrap: "wrap",
    },
    infoLabel: {
        width: 96,
        color: theme.palette.text.secondary,
        flexShrink: 0,
    },
    infoValue: {
        fontFamily: "monospace",
        wordBreak: "break-all",
    },
    infoHint: {
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(1),
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(1),
    },
    tableContainer: {
        overflowX: "auto",
    },
    copy: {
        marginLeft: theme.spacing(1),
    },
}));

export default function WebDAV() {
    const { t } = useTranslation("application", { keyPrefix: "webdav" });
    // 取消/确定属于 common 命名空间，单独取 hook，避免被 webdav 前缀拼接
    const { t: tCommon } = useTranslation("common");
    const classes = useStyles();
    const dispatch = useDispatch();

    const [tokens, setTokens] = useState([]);
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [expires, setExpires] = useState(0);
    const [root, setRoot] = useState("/");
    const [readonly, setReadonly] = useState(true);
    const [pathSelectDialog, setPathSelectDialog] = useState(false);
    const [selectedPath, setSelectedPath] = useState("");
    const [creating, setCreating] = useState(false);
    const [newToken, setNewToken] = useState("");
    const [loading, setLoading] = useState(false);

    const ToggleSnackbar = useCallback(
        (vertical, horizontal, msg, color) =>
            dispatch(toggleSnackbar(vertical, horizontal, msg, color)),
        [dispatch]
    );

    const user = Auth.GetUser();
    const serverURL = window.location.origin + "/dav";

    const copyToClipboard = (text) => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text).then(
                () => ToggleSnackbar("top", "center", t("copied"), "success"),
                () => ToggleSnackbar("top", "center", t("copyFailed"), "error")
            );
        } else {
            ToggleSnackbar("top", "center", t("copyFailed"), "error");
        }
    };

    const setMoveTarget = (folder) => {
        const path =
            folder.path === "/"
                ? folder.path + folder.name
                : folder.path + "/" + folder.name;
        setSelectedPath(path);
    };

    const selectPath = () => {
        setRoot(selectedPath === "//" ? "/" : selectedPath);
        setPathSelectDialog(false);
    };

    const loadList = () => {
        API.get("/user/token")
            .then((response) => {
                setTokens(response.data.items || []);
            })
            .catch((error) => {
                ToggleSnackbar("top", "right", error.message, "error");
            });
    };

    useEffect(() => {
        loadList();
        // eslint-disable-next-line
    }, []);

    const createToken = () => {
        if (name === "") {
            ToggleSnackbar("top", "right", t("nameRequired"), "warning");
            return;
        }
        setCreating(true);
        API.post("/user/token", {
            name: name,
            expires: expires,
            root: root,
            readonly: readonly,
        })
            .then((response) => {
                setNewToken(response.data.token);
                setName("");
                setExpires(0);
                setRoot("/");
                setReadonly(true);
                setOpen(false);
                loadList();
                ToggleSnackbar("top", "right", t("created"), "success");
            })
            .catch((error) => {
                ToggleSnackbar("top", "right", error.message, "error");
            })
            .then(() => {
                setCreating(false);
            });
    };

    const deleteToken = (id) => {
        setLoading(true);
        API.delete("/user/token", { data: { id: [id] } })
            .then(() => {
                loadList();
                ToggleSnackbar("top", "right", t("deleted"), "success");
            })
            .catch((error) => {
                ToggleSnackbar("top", "right", error.message, "error");
            })
            .then(() => {
                setLoading(false);
            });
    };

    return (
        <div className={classes.layout}>
            <Typography color="textSecondary" variant="h4">
                {t("title")}
            </Typography>

            <Paper elevation={3} className={classes.content}>
                <div className={classes.cardContent}>
                    <Typography variant="h6" gutterBottom>
                        {t("connectionInfo")}
                    </Typography>
                    <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>{t("server")}</span>
                        <span className={classes.infoValue}>{serverURL}</span>
                        <Tooltip title={t("copy")}>
                            <IconButton
                                className={classes.copy}
                                size="small"
                                onClick={() => copyToClipboard(serverURL)}
                            >
                                <FileCopy fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    </div>
                    <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>
                            {t("username")}
                        </span>
                        <span className={classes.infoValue}>
                            {user.user_name}
                        </span>
                        <span className={classes.infoHint}>
                            {t("usernameHint")}
                        </span>
                    </div>
                    <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>
                            {t("password")}
                        </span>
                        <span className={classes.infoHint}>
                            {t("passwordHint")}
                        </span>
                    </div>
                    <Alert severity="info">{t("usageHint")}</Alert>
                </div>
            </Paper>

            <div className={classes.header}>
                <Typography variant="h6">{t("myTokens")}</Typography>
                <Button
                    color="primary"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setOpen(true)}
                >
                    {t("create")}
                </Button>
            </div>

            {newToken !== "" && (
                <Alert
                    severity="warning"
                    style={{ marginBottom: 16 }}
                    action={
                        <IconButton
                            aria-label="copy"
                            color="inherit"
                            size="small"
                            onClick={() => copyToClipboard(newToken)}
                        >
                            <FileCopy fontSize="inherit" />
                        </IconButton>
                    }
                >
                    <Typography variant="body2">
                        <strong>{t("oneTimeTip")}</strong>
                    </Typography>
                    <Typography
                        variant="body2"
                        component="div"
                        style={{
                            marginTop: 8,
                            wordBreak: "break-all",
                            fontFamily: "monospace",
                        }}
                    >
                        {newToken}
                    </Typography>
                </Alert>
            )}

            <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginBottom: 12 }}
            >
                {t("tokenDes")}
            </Typography>

            <Paper elevation={3}>
                <TableContainer className={classes.tableContainer}>
                    <Table aria-label="token table" size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("name")}</TableCell>
                                <TableCell>{t("rootFolder")}</TableCell>
                                <TableCell>{t("readonly")}</TableCell>
                                <TableCell>{t("createdAt")}</TableCell>
                                <TableCell align="center">
                                    {t("actions")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tokens.map((row) => (
                                <TableRow hover key={row.ID}>
                                    <TableCell>{row.Name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={row.Root === "" ? "/" : row.Root}
                                            style={{ maxWidth: 260 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {row.Readonly ? (
                                            <Chip
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                                label={t("readOnly")}
                                            />
                                        ) : (
                                            <Chip
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                label={t("readWrite")}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {formatLocalTime(row.CreatedAt)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={t("actions")}>
                                            <IconButton
                                                disabled={loading}
                                                size="small"
                                                onClick={() =>
                                                    deleteToken(row.ID)
                                                }
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {tokens.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                padding: "40px 0",
                                            }}
                                        >
                                            <Inbox
                                                fontSize="large"
                                                color="disabled"
                                            />
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                style={{ marginTop: 8 }}
                                            >
                                                {t("empty")}
                                            </Typography>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Alert severity="info" style={{ marginTop: 16 }}>
                {t("securityTip")}
            </Alert>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="create-webdav-token-dialog"
            >
                <DialogTitle id="create-webdav-token-dialog">
                    {t("createTitle")}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("name")}
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <TextField
                        select
                        margin="dense"
                        label={t("expiresAt")}
                        fullWidth
                        value={expires}
                        onChange={(e) => setExpires(Number(e.target.value))}
                        SelectProps={{
                            native: true,
                        }}
                    >
                        {expiresOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {t(opt.label)}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        margin="dense"
                        label={t("rootFolder")}
                        fullWidth
                        value={root}
                        InputProps={{ readOnly: true }}
                        onChange={(e) => setRoot(e.target.value)}
                        helperText={t("rootFolderDes")}
                    />
                    <Button
                        color="primary"
                        startIcon={<FolderOpen />}
                        onClick={() => setPathSelectDialog(true)}
                        style={{ marginTop: 8 }}
                    >
                        {t("selectFolder")}
                    </Button>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: 16,
                        }}
                    >
                        <Switch
                            checked={readonly}
                            onChange={(e) => setReadonly(e.target.checked)}
                            color="primary"
                        />
                        <Typography variant="body2">
                            {readonly
                                ? t("readOnly")
                                : t("readWrite")}
                        </Typography>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} color="default">
                        {t("cancel")}
                    </Button>
                    <Button
                        onClick={createToken}
                        color="primary"
                        disabled={creating}
                    >
                        {t("create")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={pathSelectDialog}
                onClose={() => setPathSelectDialog(false)}
                aria-labelledby="select-webdav-root-dialog"
            >
                <DialogTitle id="select-webdav-root-dialog">
                    {t("selectFolder")}
                </DialogTitle>
                <PathSelector
                    presentPath="/"
                    selected={[]}
                    onSelect={setMoveTarget}
                />
                {selectedPath !== "" && (
                    <DialogContent
                        style={{ padding: "10px 24px 0px 24px" }}
                    >
                        <DialogContentText>
                            {t("selectedDir", { path: selectedPath })}
                        </DialogContentText>
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => setPathSelectDialog(false)}>
                        {tCommon("cancel")}
                    </Button>
                    <Button
                        onClick={selectPath}
                        color="primary"
                        disabled={selectedPath === ""}
                    >
                        {tCommon("ok")}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
